import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { uploadEvidence, getEvidence, approveEvidence, downloadEvidence, deleteEvidence, getControls, attachEvidenceFile } from '@/services/grc';
import Pagination from '@/components/Pagination';
import { Upload, Download, CheckCircle, XCircle, Shield, Trash2, AlertTriangle, Paperclip, Eye } from 'lucide-react';
import { toast } from 'sonner';
import EvidencePreviewModal from '@/components/EvidencePreviewModal';

const EvidencePage = () => {
  const [searchParams] = useSearchParams();
  const [evidenceList, setEvidenceList] = useState([]);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [attachTarget, setAttachTarget] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewEvidenceItem, setPreviewEvidenceItem] = useState(null);
  const [soc2Uploading, setSoc2Uploading] = useState(false);
  const soc2FileRef = useRef(null);

  const handleSoc2TemplateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSoc2Uploading(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('currentTenantId') || '';
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/grc/evidence/soc2/upload-template`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'X-Tenant-ID': tenantId },
          body: fd,
        },
      );
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data.detail || 'SOC 2 template upload failed');
      } else {
        const { added = 0, updated = 0, auto_mapped_control_links: mapped = 0, unmapped_row_count: unmapped = 0 } = data;
        toast.success(
          `SOC 2 template ingested — +${added} new, ~${updated} updated, ${mapped} control links${unmapped ? `, ${unmapped} rows unmapped` : ''}.`,
        );
      }
    } catch (err) {
      toast.error(err.message || 'Network error while uploading SOC 2 template');
    } finally {
      setSoc2Uploading(false);
      if (soc2FileRef.current) soc2FileRef.current.value = '';
    }
  };
  const fileInputRef = useRef(null);
  const attachInputRef = useRef(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    control_id: '',
    owner: '',
    review_frequency: 'quarterly'
  });

  const controlId = searchParams.get('control');

  useEffect(() => {
    loadControls();
    if (controlId) setFormData(prev => ({ ...prev, control_id: controlId }));
  }, [controlId]);

  useEffect(() => {
    loadEvidence();
  }, [controlId, currentPage, pageSize]);

  const loadControls = async () => {
    try {
      const data = await getControls({ page: 1, page_size: 100 });
      setControls(data.items || data);
    } catch (error) {
      console.error('Error loading controls:', error);
    }
  };

  const loadEvidence = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, page_size: pageSize };
      if (controlId) params.control_id = controlId;
      const data = await getEvidence(params);
      if (data.items) {
        setEvidenceList(data.items);
        setTotalItems(data.total);
        setTotalPages(data.total_pages);
      } else {
        setEvidenceList(data);
        setTotalItems(data.length);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error loading evidence:', error);
      toast.error('Failed to load evidence');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (size) => { setPageSize(size); setCurrentPage(1); };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 25 * 1024 * 1024) {
        toast.error('File size exceeds 25MB limit');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) { toast.error('Please select a file'); return; }
    if (!formData.title || !formData.control_id || !formData.owner) { toast.error('Please fill in required fields'); return; }
    try {
      setUploading(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      uploadFormData.append('title', formData.title);
      uploadFormData.append('control_id', formData.control_id);
      uploadFormData.append('owner', formData.owner);
      uploadFormData.append('review_frequency', formData.review_frequency);
      if (formData.description) uploadFormData.append('description', formData.description);
      await uploadEvidence(uploadFormData);
      toast.success('Evidence uploaded successfully');
      setUploadDialogOpen(false);
      resetForm();
      loadEvidence();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload evidence');
    } finally {
      setUploading(false);
    }
  };

  const handleAttachFile = async () => {
    if (!selectedFile || !attachTarget) return;
    try {
      setAttaching(true);
      const fd = new FormData();
      fd.append('file', selectedFile);
      await attachEvidenceFile(attachTarget.id, fd);
      toast.success('File attached successfully');
      setAttachDialogOpen(false);
      setAttachTarget(null);
      setSelectedFile(null);
      if (attachInputRef.current) attachInputRef.current.value = '';
      loadEvidence();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to attach file');
    } finally {
      setAttaching(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', control_id: controlId || '', owner: '', review_frequency: 'quarterly' });
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleApprove = async (evidenceId, action) => {
    try {
      await approveEvidence(evidenceId, action, '');
      toast.success(`Evidence ${action}`);
      loadEvidence();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} evidence`);
    }
  };

  const handleDownload = async (ev) => {
    try {
      const fallbackName = ev.file_name || `${(ev.title || 'evidence').replace(/[^a-z0-9\-_]+/gi, '_')}.json`;
      await downloadEvidence(ev.id, fallbackName);
      toast.success('Download started');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to download evidence');
    }
  };

  const handleDelete = async (evidenceId) => {
    if (!window.confirm('Are you sure you want to delete this evidence?')) return;
    try {
      await deleteEvidence(evidenceId);
      toast.success('Evidence deleted');
      loadEvidence();
    } catch (error) {
      toast.error('Failed to delete evidence');
    }
  };

  const getApprovalBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100 text-gray-800'} data-testid={`ev-status-${status}`}>{(status || 'required').toUpperCase()}</Badge>;
  };

  const isOverdue = (nextReviewDate) => {
    if (!nextReviewDate) return false;
    return new Date(nextReviewDate) < new Date();
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return null;
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const hasFile = (ev) => !!ev.file_name && !!ev.file_path;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="evidence-page-title">Evidence Management</h1>
          <p className="text-gray-600 mt-1">Upload and manage compliance evidence</p>
        </div>
        <div className="flex gap-2 items-center">
          {/* SOC 2 Type 1 template upload — parses the standard 4-column .xlsx
              (TSC ID / Category / Evidence Required / Description) and
              upserts into ``soc2_evidence_requirements``, merge-linked to
              catalog controls by category. Owner / admin / auditor only. */}
          <input
            ref={soc2FileRef}
            type="file"
            accept=".xlsx,.xlsm"
            className="hidden"
            onChange={handleSoc2TemplateUpload}
            data-testid="soc2-template-file-input"
          />
          <Button
            variant="outline"
            onClick={() => soc2FileRef.current?.click()}
            disabled={soc2Uploading}
            data-testid="soc2-template-upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {soc2Uploading ? 'Parsing SOC2…' : 'Upload SOC 2 Template'}
          </Button>
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="upload-evidence-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload Evidence
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Upload Evidence</DialogTitle>
              <DialogDescription>Upload evidence files (PDF, DOCX, XLSX, images - Max 25MB)</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="file">File *</Label>
                <div className="mt-2 flex items-center gap-2">
                  <Input ref={fileInputRef} id="file" type="file" accept=".pdf,.docx,.xlsx,.doc,.xls,.png,.jpg,.jpeg" onChange={handleFileSelect} className="flex-1" />
                  {selectedFile && <Badge variant="outline">{formatFileSize(selectedFile.size) || '0 Bytes'}</Badge>}
                </div>
              </div>
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Evidence title" className="mt-2" />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description" className="mt-2" rows={3} />
              </div>
              <div>
                <Label htmlFor="control">Control *</Label>
                <Select value={formData.control_id} onValueChange={(value) => setFormData({ ...formData, control_id: value })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder="Select control" /></SelectTrigger>
                  <SelectContent>
                    {controls.map(control => (
                      <SelectItem key={control.id} value={control.id}>{control.control_id} - {control.control_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="frequency">Review Frequency *</Label>
                  <Select value={formData.review_frequency} onValueChange={(value) => setFormData({ ...formData, review_frequency: value })}>
                    <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="owner">Owner *</Label>
                  <Input id="owner" value={formData.owner} onChange={(e) => setFormData({ ...formData, owner: e.target.value })} placeholder="Evidence owner" className="mt-2" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleUpload} disabled={uploading}>{uploading ? 'Uploading...' : 'Upload'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Attach File Dialog */}
      <Dialog open={attachDialogOpen} onOpenChange={(open) => { setAttachDialogOpen(open); if (!open) { setAttachTarget(null); setSelectedFile(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Attach File</DialogTitle>
            <DialogDescription>Attach a file to: <strong>{attachTarget?.title}</strong></DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input ref={attachInputRef} type="file" accept=".pdf,.docx,.xlsx,.doc,.xls,.png,.jpg,.jpeg" onChange={handleFileSelect} data-testid="attach-evidence-input" />
            {selectedFile && <p className="text-sm text-gray-500">{selectedFile.name} ({formatFileSize(selectedFile.size)})</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAttachDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAttachFile} disabled={attaching || !selectedFile} data-testid="attach-evidence-submit">
                {attaching ? 'Attaching...' : 'Attach'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Control</TableHead>
                <TableHead>File</TableHead>
                <TableHead>Review Frequency</TableHead>
                <TableHead>Next Review</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8"><div className="flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div></TableCell></TableRow>
              ) : evidenceList.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No evidence found</TableCell></TableRow>
              ) : (
                evidenceList.map((ev) => (
                  <TableRow key={ev.id} data-testid={`ev-row-${ev.id}`}>
                    <TableCell className="font-medium">{ev.title}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {controls.find(c => c.id === ev.control_id)?.control_id || ev.control_id || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {hasFile(ev) ? (
                        <>
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-gray-400" />
                            <span className="text-sm">{ev.file_name}</span>
                          </div>
                          {formatFileSize(ev.file_size) && <span className="text-xs text-gray-500">{formatFileSize(ev.file_size)}</span>}
                        </>
                      ) : (
                        <div className="flex items-center gap-1 text-xs text-amber-600">
                          <AlertTriangle className="w-3 h-3" />
                          <span>No file attached</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{ev.review_frequency || ev.collection_frequency || '—'}</Badge>
                    </TableCell>
                    <TableCell>
                      {ev.next_review_date ? (
                        <div className="flex items-center gap-1">
                          {isOverdue(ev.next_review_date) && <AlertTriangle className="w-4 h-4 text-red-500" />}
                          <span className={isOverdue(ev.next_review_date) ? 'text-red-600 font-medium' : ''}>
                            {new Date(ev.next_review_date).toLocaleDateString()}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>{getApprovalBadge(ev.approval_status || ev.status || 'required')}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-indigo-600"
                          onClick={() => { setPreviewEvidenceItem(ev); setPreviewOpen(true); }}
                          data-testid={`preview-ev-btn-${ev.id}`}
                          title="Preview evidence"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {!hasFile(ev) && !(ev.source === 'amidala' || ev.source_scan_id) && (
                          <Button size="sm" variant="ghost" className="text-blue-600" data-testid={`attach-ev-btn-${ev.id}`}
                            onClick={() => { setAttachTarget(ev); setSelectedFile(null); setAttachDialogOpen(true); }}>
                            <Paperclip className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownload(ev)}
                          data-testid={`download-ev-btn-${ev.id}`}
                          title={hasFile(ev) ? 'Download file' : 'Download generated snapshot'}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {(ev.approval_status === 'pending' || (!ev.approval_status && ev.status !== 'approved')) && (
                          <>
                            <Button size="sm" variant="ghost" className="text-green-600" data-testid={`approve-ev-btn-${ev.id}`} onClick={() => handleApprove(ev.id, 'approved')}>
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600" data-testid={`reject-ev-btn-${ev.id}`} onClick={() => handleApprove(ev.id, 'rejected')}>
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(ev.id)} data-testid={`delete-ev-btn-${ev.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} />

      <EvidencePreviewModal
        open={previewOpen}
        onOpenChange={(o) => { setPreviewOpen(o); if (!o) setPreviewEvidenceItem(null); }}
        evidence={previewEvidenceItem}
      />
    </div>
  );
};

export default EvidencePage;
